import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import PageHeader from "../../../components/PageHeader";
import Input from "../../../components/ui/Input";
import Icon from "../../../components/AppIcon";
import { cn } from "../../../utils/cn";
import FakeAppChrome from "../FakeAppChrome";
import TourNeoAiDock from "../TourNeoAiDock";
import {
  TOUR_CREATE_TEMPLATE_BLOCKS,
  TOUR_CREATE_TEMPLATE_FIELDS,
  TOUR_CREATE_TEMPLATE_MESSAGES,
  TOUR_CREATE_TEMPLATE_NAME,
  TOUR_CREATE_TEMPLATE_STATUS_LINES,
} from "../tourData";

const INITIAL_MSG = TOUR_CREATE_TEMPLATE_MESSAGES[0];
const USER_MSG = TOUR_CREATE_TEMPLATE_MESSAGES[1];
const SUCCESS_MSG = TOUR_CREATE_TEMPLATE_MESSAGES[2];

const TOKEN_RE = /\{([A-Z0-9_]+)\}/g;

function renderTemplateText(text) {
  const nodes = [];
  let lastIndex = 0;
  let match;
  const re = new RegExp(TOKEN_RE.source, "g");

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(
        <span key={`t-${lastIndex}`}>{text.slice(lastIndex, match.index)}</span>
      );
    }
    nodes.push(
      <span
        key={`tok-${match.index}-${match[1]}`}
        className="mx-0.5 inline-flex items-center whitespace-nowrap rounded-full bg-[hsl(217_91%_60%)] px-2 py-0.5 align-baseline font-mono text-[0.85em] font-semibold leading-tight text-white"
      >
        {match[1]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(<span key={`t-end-${lastIndex}`}>{text.slice(lastIndex)}</span>);
  }

  return nodes;
}

function collectKeysFromBlocks(blocks, count) {
  const keys = new Set();
  blocks.slice(0, count).forEach((block) => {
    const re = new RegExp(TOKEN_RE.source, "g");
    let match;
    while ((match = re.exec(block.text)) !== null) {
      keys.add(match[1]);
    }
  });
  return keys;
}

const CreateTemplateScene = () => {
  const [messages, setMessages] = useState([INITIAL_MSG]);
  const [loading, setLoading] = useState(false);
  const [composerText, setComposerText] = useState("");
  const [visibleBlocks, setVisibleBlocks] = useState(0);
  const [templateName, setTemplateName] = useState("");

  useEffect(() => {
    const timers = [];

    timers.push(
      window.setTimeout(() => {
        setComposerText("");
        let i = 0;
        const full = USER_MSG.text;
        const typeId = window.setInterval(() => {
          i += 4;
          setComposerText(full.slice(0, i));
          if (i >= full.length) window.clearInterval(typeId);
        }, 14);
        timers.push(typeId);
      }, 450)
    );

    timers.push(
      window.setTimeout(() => {
        setComposerText("");
        setMessages([INITIAL_MSG, USER_MSG]);
        setLoading(true);
      }, 2600)
    );

    timers.push(
      window.setTimeout(() => {
        setLoading(false);
        setMessages([INITIAL_MSG, USER_MSG, SUCCESS_MSG]);
        setTemplateName(TOUR_CREATE_TEMPLATE_NAME);
        let n = 0;
        const revealId = window.setInterval(() => {
          n += 1;
          setVisibleBlocks(n);
          if (n >= TOUR_CREATE_TEMPLATE_BLOCKS.length) {
            window.clearInterval(revealId);
          }
        }, 280);
        timers.push(revealId);
      }, 5000)
    );

    return () => {
      timers.forEach((id) => {
        window.clearTimeout(id);
        window.clearInterval(id);
      });
    };
  }, []);

  const visibleFieldKeys = useMemo(
    () => collectKeysFromBlocks(TOUR_CREATE_TEMPLATE_BLOCKS, visibleBlocks),
    [visibleBlocks]
  );

  const visibleFields = TOUR_CREATE_TEMPLATE_FIELDS.filter((field) =>
    visibleFieldKeys.has(field.key)
  );

  return (
    <FakeAppChrome activeNavId="create-template">
      <div className="relative flex min-h-full flex-col p-4 sm:p-6">
        <PageHeader
          title="Criar Modelo"
          subtitle="Monte seu modelo com campos dinâmicos e edite o conteúdo como em um editor de texto."
          icon="LayoutTemplate"
          className="max-sm:mb-3 mb-4"
        />

        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card/95 px-3 py-2.5 shadow-sm sm:mb-4">
          <span className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1.5 text-xs font-medium text-muted-foreground">
            <Icon name="Coins" size={14} />
            120 Neo Coins
          </span>
          <span className="text-xs text-muted-foreground">
            Importe PDF/DOCX · edite manualmente · ou use a NeoAi
          </span>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row lg:gap-6">
          <aside className="flex w-full shrink-0 flex-col rounded-lg border border-border bg-card shadow-elevation-sm lg:w-[30%] lg:min-w-[240px] lg:max-w-[360px]">
            <div className="shrink-0 space-y-3 border-b border-border p-4">
              <Input
                label="Nome do Modelo"
                value={templateName}
                placeholder="Ex.: Contrato de Locação"
                readOnly
                tabIndex={-1}
              />
              <div className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary/10 px-3 py-2 text-sm font-medium text-primary">
                <Icon name="Plus" size={16} />
                Adicionar Campo
              </div>
            </div>
            <div className="min-h-[8rem] flex-1 space-y-2 overflow-y-auto p-3">
              {visibleFields.length === 0 ? (
                <p className="px-1 py-2 text-xs text-muted-foreground">
                  Os campos do modelo aparecerão aqui conforme a NeoAi gera o
                  texto.
                </p>
              ) : (
                <AnimatePresence initial={false}>
                  {visibleFields.map((field) => (
                    <motion.div
                      key={field.key}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="rounded-lg border border-border bg-background px-3 py-2.5"
                    >
                      <p className="font-mono text-xs font-semibold text-primary">
                        {`{${field.key}}`}
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {field.label} · {field.type}
                      </p>
                    </motion.div>
                  ))}
                </AnimatePresence>
              )}
            </div>
          </aside>

          <div className="relative min-h-[20rem] flex-1 rounded-lg border border-border bg-card shadow-soft-sm">
            <div className="min-h-[20rem] space-y-4 p-4 sm:p-6 lg:pr-[min(24rem,42%)]">
              {visibleBlocks === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {loading
                    ? "A NeoAi está montando o modelo com placeholders…"
                    : "Editor vazio — descreva o modelo desejado para a NeoAi."}
                </p>
              ) : (
                <AnimatePresence initial={false}>
                  {TOUR_CREATE_TEMPLATE_BLOCKS.slice(0, visibleBlocks).map(
                    (block, index) => (
                      <motion.div
                        key={`${block.type}-${index}`}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.28 }}
                      >
                        {block.type === "heading" ? (
                          <h2 className="text-base font-bold tracking-tight text-foreground sm:text-lg">
                            {block.text}
                          </h2>
                        ) : (
                          <p className="text-sm leading-relaxed text-foreground sm:text-[0.95rem]">
                            {renderTemplateText(block.text)}
                          </p>
                        )}
                      </motion.div>
                    )
                  )}
                </AnimatePresence>
              )}
            </div>

            <div className="pointer-events-none absolute bottom-4 right-4 z-[2] hidden lg:block">
              <div className="pointer-events-auto">
                <TourNeoAiDock
                  messages={messages}
                  loading={loading}
                  composerText={composerText}
                  placeholder="Ex.: Elabore um modelo de autorização de venda…"
                  loadingVariant="magic"
                  statusLines={TOUR_CREATE_TEMPLATE_STATUS_LINES}
                />
              </div>
            </div>
          </div>
        </div>

        <div className={cn("mt-4 lg:hidden")}>
          <TourNeoAiDock
            messages={messages}
            loading={loading}
            composerText={composerText}
            placeholder="Ex.: Elabore um modelo de autorização de venda…"
            loadingVariant="magic"
            statusLines={TOUR_CREATE_TEMPLATE_STATUS_LINES}
          />
        </div>
      </div>
    </FakeAppChrome>
  );
};

export default CreateTemplateScene;
